import * as Tabs from '@radix-ui/react-tabs';
import { ArrowLeftRight, ArrowRight, Check, ExternalLink, FileText, Globe, Inbox, Lightbulb, Save, Sparkles, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { StatCard } from '../components/ui/data';
import { ConfirmDialog, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Textarea,
} from '../components/ui/primitives';
import { LaunchAgentModal } from '../components/jobs/LaunchAgentModal';
import { useLeadTypes, useProject, useStats, useUpdateProject } from '../hooks/queries';
import type { ProjectKind } from '../types/api';
import { formatNumber, formatPercent } from '../lib/utils';

const PROMPT_FIELDS: Array<{
  key: 'productContext' | 'researchPrompt' | 'outreachPrompt' | 'contentPrompt';
  label: string;
  hint: string;
  placeholder: string;
  kinds: ProjectKind[];
}> = [
  {
    key: 'productContext',
    label: 'Product context',
    hint: 'What you sell, who it is for, which numbers may be quoted. Every agent gets this verbatim.',
    kinds: ['OUTREACH', 'CONTENT'],
    placeholder:
      'RentX is a peer-to-peer rental marketplace…\n\nWhy partners care:\n- Affiliates earn a commission per booking\n- B2B partners earn referral fees\n\nLive in: NL, BE, DE.',
  },
  {
    key: 'researchPrompt',
    label: 'Research prompt',
    hint: 'House rules for finding leads: the quality bar, whether to guess emails, how to score. Lead types add their own instructions on top.',
    kinds: ['OUTREACH'],
    placeholder:
      'Prefer depth over volume…\nOnly record an email address you have actually seen.\nScore 80+ = obvious fit, 50-79 = plausible, under 50 = do not save.',
  },
  {
    key: 'outreachPrompt',
    label: 'Outreach prompt',
    hint: 'House style for every email: length, tone, banned phrases, language rules. This is what keeps the writing consistent.',
    kinds: ['OUTREACH'],
    placeholder:
      'Under 150 words. One ask.\nOpen with something only true of this recipient.\nNo "I hope this email finds you well".\nWrite in the recipient language when it is Dutch, German or French.',
  },
  {
    key: 'contentPrompt',
    label: 'Content prompt',
    hint: 'House style and SEO/GEO rules for every blog post: structure, tone, heading conventions, internal links, what to cite and how.',
    kinds: ['CONTENT'],
    placeholder:
      'Lead with the answer, then the detail — write for AI answer engines as much as for readers.\nUse H2s that read as questions. One clear takeaway per section.\nLink to the pricing and signup pages where natural.\nWrite in the site language.',
  },
];

export const ProjectOverviewPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading, error } = useProject(projectId);
  const { data: leadTypes } = useLeadTypes(projectId);
  const { data: stats } = useStats(projectId);
  const updateProject = useUpdateProject(projectId ?? '');
  const toast = useToast();

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [launchOpen, setLaunchOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  // Reset the editing buffer whenever a different project loads.
  useEffect(() => {
    if (!project) return;
    setDraft({
      name: project.name,
      description: project.description ?? '',
      websiteUrl: project.websiteUrl ?? '',
      status: project.status,
      productContext: project.productContext ?? '',
      researchPrompt: project.researchPrompt ?? '',
      outreachPrompt: project.outreachPrompt ?? '',
      contentPrompt: project.contentPrompt ?? '',
    });
  }, [project]);

  if (isLoading) return <LoadingBlock />;
  if (error) return <Page><ErrorBlock error={error} /></Page>;
  if (!project) return <Page><ErrorBlock error={new Error('Project not found')} /></Page>;

  const kind: ProjectKind = project.kind;
  const isContent = kind === 'CONTENT';
  const otherKind: ProjectKind = isContent ? 'OUTREACH' : 'CONTENT';
  const promptFields = PROMPT_FIELDS.filter((field) => field.kinds.includes(kind));

  const dirty = Object.entries(draft).some(([key, value]) => {
    const current = (project as unknown as Record<string, unknown>)[key];
    return (current ?? '') !== value;
  });

  const switchKind = async () => {
    try {
      await updateProject.mutateAsync({ kind: otherKind });
      setConfirmSwitch(false);
      toast.success(`Switched to a ${otherKind === 'CONTENT' ? 'Content' : 'Outreach'} project`);
    } catch (error) {
      toast.error(error);
    }
  };

  const save = async () => {
    try {
      await updateProject.mutateAsync(draft);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      toast.success('Project saved');
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <>
    <LaunchAgentModal key={project.id} open={launchOpen} onClose={() => setLaunchOpen(false)} projectId={project.id} />
    <Page>
      <PageHeader
        title={project.name}
        description={isContent ? 'Plan your content, review drafts, and guide your writing agents.' : 'Manage your outreach and give your agents the context they need.'}
        actions={
          <>
            <Button onClick={() => setLaunchOpen(true)}>Launch agent</Button>
            {project.websiteUrl ? (
              <a href={project.websiteUrl} target="_blank" rel="noreferrer">
                <Button variant="ghost" icon={<ExternalLink className="h-4 w-4" />}>
                  Visit site
                </Button>
              </a>
            ) : null}
            <Button
              variant="primary"
              onClick={save}
              disabled={!dirty || !draft.name?.trim()}
              loading={updateProject.isPending}
              icon={saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            >
              {saved ? 'Saved' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </>
        }
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {(isContent ? [
          { to: 'topics', title: 'Plan topics', description: 'Build your content backlog', icon: Lightbulb },
          { to: 'posts?status=DRAFT', title: 'Review posts', description: 'Check drafts before publishing', icon: FileText },
          { to: 'content-channel', title: 'Publishing', description: 'Manage your channel and schedule', icon: Globe },
        ] : [
          { to: 'leads', title: 'Explore leads', description: 'Find contacts for your next campaign', icon: Users },
          { to: 'emails?status=DRAFT', title: 'Review emails', description: 'Approve drafts before sending', icon: FileText },
          { to: 'inbox', title: 'Open inbox', description: 'Follow up on incoming replies', icon: Inbox },
        ]).map(({ to, title, description, icon: Icon }) => (
          <Link key={to} to={`/projects/${project.id}/${to}`} className="group flex items-center gap-3 rounded-xl border border-line bg-surface p-4 shadow-card transition-colors hover:border-brand/30 hover:bg-brand/[0.03]">
            <Icon className="h-5 w-5 shrink-0 text-brand" />
            <div className="min-w-0 flex-1"><p className="text-[13px] font-semibold">{title}</p><p className="mt-1 text-xs text-muted">{description}</p></div>
            <ArrowRight className="h-4 w-4 shrink-0 text-faint group-hover:text-brand" />
          </Link>
        ))}
      </div>

      {isContent ? (
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Topics" value={formatNumber(project._count?.blogTopics ?? 0)} tone="brand" />
          <StatCard label="Posts" value={formatNumber(project._count?.blogPosts ?? 0)} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="Leads" value={formatNumber(stats?.totals.leads ?? 0)} tone="brand" />
          <StatCard label="Emails sent" value={formatNumber(stats?.totals.emailsSent ?? 0)} />
          <StatCard
            label="Replies"
            value={formatNumber(stats?.totals.repliesReceived ?? 0)}
            sub={`${formatPercent(stats?.totals.replyRate ?? 0)} reply rate`}
            tone="positive"
          />
          <StatCard
            label="Awaiting review"
            value={formatNumber(stats?.totals.drafts ?? 0)}
            sub="drafts from agents"
            tone={(stats?.totals.drafts ?? 0) > 0 ? 'warning' : 'neutral'}
          />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="self-start lg:col-span-2">
          <CardHeader
            title="Prompts"
            description={
              isContent
                ? 'The context that decides how good your blog posts are.'
                : 'The context that decides how good your leads and emails are.'
            }
          />
          <Tabs.Root defaultValue="productContext">
            <Tabs.List aria-label="Agent instructions" className="flex flex-wrap gap-1 border-b border-line px-4 pt-3">
              {promptFields.map((field) => <Tabs.Trigger key={field.key} value={field.key} className="border-b-2 border-transparent px-3 py-3 text-[13px] font-medium text-muted hover:text-ink data-[state=active]:border-brand data-[state=active]:text-brand">
                {field.key === 'productContext' ? 'Product context' : field.key === 'researchPrompt' ? 'Research' : field.key === 'outreachPrompt' ? 'Email style' : 'Content style'}
              </Tabs.Trigger>)}
            </Tabs.List>
            {promptFields.map((field) => (
              <Tabs.Content key={field.key} value={field.key} className="p-5">
              <Field label={field.label} hint={field.hint} htmlFor={field.key}>
                <Textarea
                  id={field.key}
                  rows={16}
                  className="prompt-input"
                  value={draft[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
                />
              </Field>
              </Tabs.Content>
            ))}
          </Tabs.Root>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Settings" />
            <CardBody className="space-y-4">
              <Field label="Name" htmlFor="name">
                <Input
                  id="name"
                  value={draft.name ?? ''}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </Field>
              <Field label="Description" htmlFor="description">
                <Input
                  id="description"
                  value={draft.description ?? ''}
                  onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                />
              </Field>
              <Field label="Website" htmlFor="websiteUrl">
                <Input
                  id="websiteUrl"
                  value={draft.websiteUrl ?? ''}
                  onChange={(event) => setDraft({ ...draft, websiteUrl: event.target.value })}
                />
              </Field>
              <Field
                label="Status"
                htmlFor="status"
                hint="Paused projects are skipped by the job queue and the send worker."
              >
                <Select
                  id="status"
                  value={draft.status ?? 'ACTIVE'}
                  onChange={(event) => setDraft({ ...draft, status: event.target.value })}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="ARCHIVED">Archived</option>
                </Select>
              </Field>
              <Field
                label="Project type"
                hint="Switch between outreach and content. Existing data is preserved."
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge tone="brand">{isContent ? 'Content' : 'Outreach (GTM)'}</Badge>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ArrowLeftRight className="h-4 w-4" />}
                    onClick={() => setConfirmSwitch(true)}
                  >
                    Switch to {otherKind === 'CONTENT' ? 'Content' : 'Outreach'}
                  </Button>
                </div>
              </Field>
            </CardBody>
          </Card>

          {!isContent && (
            <>
          <Card>
            <CardHeader
              title="Lead types"
              description="Each one has its own research prompt"
              actions={
                <Link to={`/projects/${project.id}/lead-types`}>
                  <Button variant="ghost" size="sm">
                    Manage
                  </Button>
                </Link>
              }
            />
            {leadTypes?.length ? (
              <div className="divide-y divide-line">
                {leadTypes.map((type) => (
                  <div key={type.id} className="flex items-center justify-between gap-3 px-5 py-2.5">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ background: type.color }}
                      />
                      <span className="truncate text-[13px] text-ink">{type.name}</span>
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-faint">
                      {formatNumber(type.stats?.leads ?? 0)} / {formatNumber(type.targetCount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <CardBody>
                <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center">
                  <Sparkles className="mx-auto mb-2 h-5 w-5 text-faint" />
                  <p className="text-[13px] text-muted">
                    No lead types yet. This is where the research agent gets its instructions.
                  </p>
                  <Link to={`/projects/${project.id}/lead-types`}>
                    <Button variant="primary" size="sm" className="mt-3">
                      Add a lead type
                    </Button>
                  </Link>
                </div>
              </CardBody>
            )}
          </Card>

          <Card>
            <CardHeader title="Mailbox" description="Reading and sending" />
            <CardBody>
              {project.mailboxes?.length ? (
                <div className="space-y-2">
                  {project.mailboxes.map((mailbox) => (
                    <div
                      key={mailbox.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-medium text-ink">
                          {mailbox.fromEmail}
                        </div>
                        <div className="text-2xs text-faint">
                          {mailbox.dailyLimit}/day · {mailbox.sendWindowStart}:00–
                          {mailbox.sendWindowEnd}:00 {mailbox.timezone}
                        </div>
                      </div>
                      {mailbox.isDefault ? <Badge tone="brand">default</Badge> : null}
                    </div>
                  ))}
                  <Link to={`/projects/${project.id}/mailbox`}>
                    <Button variant="secondary" size="sm" className="w-full">
                      Mailbox settings
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center">
                  <p className="text-[13px] text-muted">
                    No mailbox yet. Nothing can be sent until one is configured.
                  </p>
                  <Link to={`/projects/${project.id}/mailbox`}>
                    <Button variant="primary" size="sm" className="mt-3">
                      Add a mailbox
                    </Button>
                  </Link>
                </div>
              )}
            </CardBody>
          </Card>
            </>
          )}

          {isContent && (
            <>
              <Card>
                <CardHeader
                  title="Topics"
                  description="The blog backlog"
                  actions={
                    <Link to={`/projects/${project.id}/topics`}>
                      <Button variant="ghost" size="sm">
                        Manage
                      </Button>
                    </Link>
                  }
                />
                <CardBody>
                  {(project._count?.blogTopics ?? 0) > 0 ? (
                    <div className="flex items-center gap-2 text-[13px] text-muted">
                      <Lightbulb className="h-4 w-4 text-faint" />
                      {formatNumber(project._count?.blogTopics ?? 0)} topic(s) ·{' '}
                      {formatNumber(project._count?.blogPosts ?? 0)} post(s)
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center">
                      <Sparkles className="mx-auto mb-2 h-5 w-5 text-faint" />
                      <p className="text-[13px] text-muted">
                        No topics yet. This is the backlog the content agent drafts from.
                      </p>
                      <Link to={`/projects/${project.id}/topics`}>
                        <Button variant="primary" size="sm" className="mt-3">
                          Add topics
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Publishing" description="Where posts go live" />
                <CardBody>
                  <Link to={`/projects/${project.id}/content-channel`}>
                    <Button variant="secondary" size="sm" className="w-full" icon={<Globe className="h-4 w-4" />}>
                      Content channel settings
                    </Button>
                  </Link>
                  <Link to={`/projects/${project.id}/posts`}>
                    <Button variant="ghost" size="sm" className="mt-2 w-full" icon={<FileText className="h-4 w-4" />}>
                      Review posts
                    </Button>
                  </Link>
                </CardBody>
              </Card>
            </>
          )}
        </div>
      </div>
      {dirty && <div role="status" className="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand/20 bg-surface px-5 py-3 shadow-pop">
        <span className="text-[13px] font-medium">You have unsaved changes</span>
        <Button variant="primary" onClick={save} disabled={!draft.name?.trim()} loading={updateProject.isPending} icon={<Save className="h-4 w-4" />}>Save changes</Button>
      </div>}
    </Page>

    <ConfirmDialog
      open={confirmSwitch}
      onClose={() => setConfirmSwitch(false)}
      title={`Switch to a ${otherKind === 'CONTENT' ? 'Content' : 'Outreach'} project?`}
      confirmLabel={`Switch to ${otherKind === 'CONTENT' ? 'Content' : 'Outreach'}`}
      loading={updateProject.isPending}
      message={
        isContent
          ? 'This project will show outreach features (leads, campaigns, email) instead of content. Your topics and posts are kept and reappear if you switch back.'
          : 'This project will show content features (topics, posts, publishing) instead of outreach. Your leads, campaigns and emails are kept and reappear if you switch back.'
      }
      onConfirm={switchKind}
    />
    </>
  );
};
